import { useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/ui-components/ui/button";
import { useRouter } from "@/hooks/useRouter";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/ui-components/ui/dropdown-menu";
import { LogOut, HelpCircle, ArrowLeft, Code, Key, User, Box } from "lucide-react";
import { useUser, UserProfile } from "@clerk/clerk-react";
import { useOptimizedAuthContext } from "@/contexts/OptimizedAuthContext";
import { useEntitlement } from "@/contexts/EntitlementContext";
import { useFlag } from "@/contexts/FlagsContext";
import { useResponsive } from "@/contexts/ResponsiveContext";
import { CompactUnifiedSearch } from "../search/CompactUnifiedSearch";
import { useShell } from "@/components/layout/shell-context";

interface AppHeaderProps {
  showSearch?: boolean;
  showBackButton?: boolean;
  searchQuery?: string;
  onSearchQueryChange?: (query: string) => void;
  onSearchActive?: (active: boolean) => void;
  onSearchResults?: (results: unknown[]) => void;
  onNavigateBack?: () => void;
  onNavigateToChat?: (initialQuery: string) => void;
  onOpenExchange?: (exchangeId: string) => void;
  onNavigateToProject?: (projectId: string) => void;
  projects?: unknown[];
  isSearchActive?: boolean;
  searchPlaceholder?: string;
}

export function AppHeader({
  showSearch = false,
  showBackButton = false,
  onNavigateBack,
}: AppHeaderProps) {
  const router = useRouter();
  const { inShell } = useShell();
  const { signOut } = useOptimizedAuthContext();
  const { plan, can, promptUpgrade } = useEntitlement();
  const showSkills = useFlag("skills");
  const showBilling = useFlag("billing");
  const planLabel = plan.charAt(0).toUpperCase() + plan.slice(1);
  const isPaidPlan = plan !== "free";
  const { user } = useUser();
  const [showProfileModal, setShowProfileModal] = useState(false);

  // Compact below 768px: the inline search bar needs ~700px of header width
  // (logo ~150px + profile ~150px + padding ~100px + 300px search minimum).
  const { isMobile, breakpoint } = useResponsive();
  const isCompactLayout = isMobile || breakpoint !== "desktop";

  const iconLetters =
    (user?.fullName ?? user?.emailAddresses?.[0]?.emailAddress)?.[0]?.toUpperCase() ||
    "U";

  // Inside the app shell the ribbon owns the logo, nav, search, and profile, so this
  // legacy header collapses to just a back affordance (or nothing).
  if (inShell) {
    if (!showBackButton || !onNavigateBack) return null;
    return (
      <header className="sticky top-0 z-[40] h-12 flex items-center px-4 bg-background/90 backdrop-blur-sm border-b border-border">
        <Button
          variant="ghost"
          size="sm"
          onClick={onNavigateBack}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden lg:inline">Back</span>
        </Button>
      </header>
    );
  }

  return (
    <>
      <header className="sticky top-0 z-[60] bg-card/90 backdrop-blur-sm border-b border-border">
      <div className="flex flex-col">
        {/* Top bar with logo, centered search/back button, and profile */}
        <div className="relative h-[64px] flex items-center justify-between px-6">
          {/* Left section: Logo (always in top left) - responsive width */}
          <div
            className={`flex-shrink-0 ${isCompactLayout ? "w-auto" : "w-[100px] sm:w-[120px] lg:w-[150px]"} ${isCompactLayout ? "mr-0" : "mr-4 lg:mr-6"}`}
          >
            <a href="/" className="inline-flex items-center">
              <img
                src="/specstorylogo.svg"
                alt="SpecStory"
                className="h-6 w-auto hover:scale-105 transition-transform"
              />
            </a>
          </div>

          {/* Center section: Back button and Search bar (truly centered on desktop only) */}
          {showSearch && !isCompactLayout && (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex items-center gap-3 max-w-4xl w-full">
                {showBackButton && onNavigateBack && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onNavigateBack}
                    className="flex items-center gap-2 text-muted-foreground hover:text-foreground flex-shrink-0"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span className="hidden lg:inline">Back</span>
                  </Button>
                )}
                <div className="flex-1">
                  <CompactUnifiedSearch />
                </div>
              </div>
            </div>
          )}

          {/* If no search bar but not compact, fill center space */}
          {!showSearch && !isCompactLayout && (
            <div className="flex-1 flex items-center justify-center">
              {showBackButton && onNavigateBack && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onNavigateBack}
                  className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span className="hidden lg:inline">Back</span>
                </Button>
              )}
            </div>
          )}

          {/* Mobile: Centered back button when no search */}
          {!showSearch &&
            isCompactLayout &&
            showBackButton &&
            onNavigateBack && (
              <div className="flex-1 flex items-center justify-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onNavigateBack}
                  className="p-2 text-muted-foreground hover:text-foreground"
                >
                  <ArrowLeft className="w-4 h-4" />
                </Button>
              </div>
            )}

          {/* Mobile: Empty space when no search and no back button */}
          {!showSearch && isCompactLayout && !showBackButton && (
            <div className="flex-1" />
          )}

          {/* Right section: Profile dropdown (always in top right) - responsive width */}
          <div
            className={`flex-shrink-0 flex justify-end items-center gap-2 ${isCompactLayout ? "w-auto" : "w-[100px] sm:w-[120px] lg:w-[150px]"} ${isCompactLayout ? "ml-0" : "ml-4 lg:ml-6"}`}
          >
            {/* Profile Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="rounded-full p-0 hover:bg-muted"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#01B9D9] to-[#0095B6] flex items-center justify-center overflow-hidden">
                    {user?.imageUrl ? (
                      <img
                        src={user.imageUrl}
                        alt={user.fullName || 'Profile'}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-white text-sm font-medium">
                        {iconLetters}
                      </span>
                    )}
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-2">
                  <p className="text-sm font-medium text-foreground">
                    {user?.fullName}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {user?.emailAddresses?.[0]?.emailAddress}
                  </p>
                  <div className="mt-1.5 flex items-center gap-2">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        isPaidPlan
                          ? "bg-[#01B9D9]/10 text-[#0095B6]"
                          : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {planLabel} plan
                    </span>
                    {showBilling && (
                      <button
                        type="button"
                        onClick={() => router.push("/billing")}
                        className="text-[11px] font-medium text-[#0095B6] hover:underline underline-offset-2"
                      >
                        Billing
                      </button>
                    )}
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => setShowProfileModal(true)}
                >
                  <User className="w-4 h-4 mr-2" />
                  Manage Profile
                </DropdownMenuItem>
                <DropdownMenuItem className="cursor-pointer">
                  <a
                    href="/api-keys"
                    target="_blank"
                    className="whitespace-nowrap flex items-center gap-2"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.preventDefault();
                      router.push("/api-keys");
                    }}
                  >
                    <Key className="w-4 h-4 mr-2" />
                    API Keys
                  </a>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {showSkills && (
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() =>
                      can("skills")
                        ? router.push("/skills")
                        : promptUpgrade("skills")
                    }
                  >
                    <Box className="w-4 h-4 mr-2" />
                    Skills
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem className="cursor-pointer">
                  <a
                    href="https://docs.specstory.com/cloud/overview"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="whitespace-nowrap flex items-center gap-2"
                  >
                    <HelpCircle className="w-4 h-4 mr-2" />
                    Help & Support
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem className="cursor-pointer">
                  <a
                    href="https://docs.specstory.com/api-reference/introduction"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="whitespace-nowrap flex items-center gap-2"
                  >
                    <Code className="w-4 h-4 mr-2" />
                    API Documentation
                  </a>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="cursor-pointer text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => {
                    signOut();
                    router.push("/");
                  }}
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      </header>

      {/* Profile Management Modal - Rendered via Portal outside header */}
      {showProfileModal && createPortal(
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4 overflow-y-auto"
          onClick={() => setShowProfileModal(false)}
        >
          <div
            className="w-full max-w-2xl my-8"
            onClick={(e) => e.stopPropagation()}
          >
            <UserProfile
              appearance={{
                elements: {
                  rootBox: "w-full",
                  card: "shadow-2xl rounded-lg"
                },
                variables: {
                  colorPrimary: "#01B9D9"
                }
              }}
            />
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
